import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoCustoType {
    id: number;
    description: string;
    value?: number;
    isFinancial: boolean;
    isFixo: boolean;
    empresaId: number;
}

export interface CreateTipoCustoType {
    description: string;
    value?: number;
    isFinancial: boolean;
    isFixo: boolean;
    empresaId: number;
}

export interface UpdateTipoCustoType {
    id: number;
    description: string;
    value?: number;
    isFinancial: boolean;
    isFixo: boolean;
}

export default function useTipoCusto() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoCusto, setListTipoCusto] = useState<TipoCustoType[]>([]);
    const [tipoCustoPageCount, setTipoCustoPageCount] = useState<number>(0);
    const [initialListTipoCusto, setInitialListTipoCusto] = useState<TipoCustoType[]>([]);
    const [tipoCusto, setTipoCusto] = useState<TipoCustoType>({
        id: 0,
        description: "",
        value: 0,
        isFinancial: false,
        isFixo: false,
        empresaId: 0,
    });

    function createTipoCusto(createTipoCustoType: CreateTipoCustoType): Promise<CreateTipoCustoType | null> {
        setLoading(true);
        return api
            .post(`/TipoCusto/create`, createTipoCustoType)
            .then((response) => {
                const createdTipoCusto: CreateTipoCustoType = response.data;
                showToast("success", "Tipo de custo criada com sucesso.");
                setLoading(false);
                return createdTipoCusto;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar tipo de custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar tipo de custo:", error);
                return null;
            });
    }

    function getAllTipoCustoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
        isFinancial?: boolean,
        description?: string
    ): Promise<TipoCustoType[] | null> {
        setLoading(true);
        let url = `/TipoCusto/getAllTipoCustoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (isFinancial !== undefined) {
            url += `&isFinancial=${isFinancial}`;
        }

        if (description) {
            url += `&description=${description}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoCusto(response.data.result);
                setInitialListTipoCusto(response.data.result);
                setTipoCustoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar tipo de custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar tipo de custo:", error);
                return null;
            });
    }

    function updateTipoCusto(updateTipoCustoData: UpdateTipoCustoType): Promise<UpdateTipoCustoType | null> {
        setLoading(true);
        return api
            .put(`/TipoCusto/${updateTipoCustoData.id}`, updateTipoCustoData)
            .then((response) => {
                showToast("success", "Tipo de custo atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar tipo de custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar tipo de custo:", error);
                return null;
            });
    }

    function deleteTipoCusto(tipoCustoId: number): Promise<TipoCustoType | null> {
        setLoading(true);
        return api
            .delete(`/TipoCusto/${tipoCustoId}`)
            .then(() => {
                setTipoCusto({
                    id: 0,
                    description: "",
                    isFinancial: false,
                    isFixo: false,
                    empresaId: 0,
                });
                showToast("success", "Tipo de custo deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar tipo de custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar tipo de custo:", error);
                return null;
            });
    }

    return {
        createTipoCusto,
        getAllTipoCustoPaged,
        updateTipoCusto,
        deleteTipoCusto,
        setListTipoCusto,
        listTipoCusto,
        setTipoCustoPageCount,
        tipoCustoPageCount,
        setInitialListTipoCusto,
        initialListTipoCusto,
        setTipoCusto,
        tipoCusto,
    };
}